import { ColocaSkeleton } from "@/shared/components/feedback/ColocaSkeleton";
import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";
import { formatDateTime } from "@/shared/utils/dateTimeHelpers";

import { bucketForStatus } from "../constants/paymentsActivity.constants";
import type { PaymentStatusChange } from "../services/paymentsStatusChanges.service";
import type { ActivityRow } from "../types/paymentsActivity.types";

export interface OperationTimelineProps {
  row: ActivityRow;
  /** Real transition log (INTERNAL users). Null → render the derived timeline from entity timestamps. */
  timeline: PaymentStatusChange[] | null;
  isTimelineLoading: boolean;
}

interface TimelineStep {
  key: string;
  label: string;
  caption: string | null;
  time: string | null;
  tone: "done" | "active" | "failed" | "cancelled";
}

function Dot({ tone }: { tone: TimelineStep["tone"] }) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        "mt-1.5 size-2 shrink-0 rounded-full",
        tone === "done" && "bg-success",
        tone === "active" && "animate-pulse bg-primary",
        tone === "failed" && "bg-destructive",
        // A cancellation is a deliberate release of funds, not a failure — neutral, never red.
        tone === "cancelled" && "bg-muted-foreground",
      )}
    />
  );
}

function Steps({ steps }: { steps: TimelineStep[] }) {
  return (
    <ol className="space-y-0">
      {steps.map((step, index) => (
        <li key={step.key} className="relative flex gap-3 pb-4 last:pb-0">
          {index < steps.length - 1 && (
            <span aria-hidden="true" className="absolute left-[3.5px] top-4 h-full w-px bg-border" />
          )}
          <Dot tone={step.tone} />
          <div className="min-w-0">
            <p className="text-sm font-medium text-foreground">{step.label}</p>
            {step.caption && <p className="text-xs text-muted-foreground">{step.caption}</p>}
            {step.time && (
              <p className="font-mono text-[11px] text-muted-foreground tabular-nums">
                {formatDateTime(step.time)}
              </p>
            )}
          </div>
        </li>
      ))}
    </ol>
  );
}

/**
 * Vertical status timeline. INTERNAL users see the real append-only transition log (oldest → newest, with a
 * provider-neutral source label and the reason on failures); org members — who get 403 on that endpoint — see
 * a lightweight timeline derived from the entity's own timestamps (created → in process → settled / failed).
 * Never renders the raw provider identity.
 */
export function OperationTimeline({ row, timeline, isTimelineLoading }: OperationTimelineProps) {
  const { t } = useTranslations("paymentsActivity");

  if (isTimelineLoading) {
    return (
      <div className="space-y-2" aria-hidden="true">
        <ColocaSkeleton className="h-4 w-2/3" />
        <ColocaSkeleton className="h-4 w-1/2" />
        <ColocaSkeleton className="h-4 w-3/5" />
      </div>
    );
  }

  // Real timeline (INTERNAL): one step per recorded transition.
  if (timeline && timeline.length > 0) {
    const sourceLabel: Record<string, string> = {
      internal_api: t("timeline_source_staff"),
      provider_webhook: t("timeline_source_network"),
      system: t("timeline_source_system"),
    };
    const steps: TimelineStep[] = timeline.map((change, index) => {
      const isLast = index === timeline.length - 1;
      // Derive the tone from the same bucket map the badge uses, per direction. A hardcoded "failed" list
      // here used to swallow CANCELLED, which the map deliberately classifies as neutral.
      const bucket = bucketForStatus(row.direction, change.new_status);
      const tone: TimelineStep["tone"] =
        bucket === "failed"
          ? "failed"
          : bucket === "cancelled"
            ? "cancelled"
            : isLast && row.isNonTerminal
              ? "active"
              : "done";
      return {
        key: change.id,
        label: change.previous_status
          ? `${change.previous_status} → ${change.new_status}`
          : change.new_status,
        caption: [sourceLabel[change.source] ?? change.source, change.reason].filter(Boolean).join(" · "),
        time: change.created_at,
        tone,
      };
    });
    return <Steps steps={steps} />;
  }

  // Derived timeline (org members): created → in process → settled / failed / cancelled, from entity
  // timestamps.
  const steps: TimelineStep[] = [
    {
      key: "created",
      label: t("timeline_step_created"),
      caption: null,
      time: row.createdAt,
      tone: "done",
    },
  ];
  if (row.failedAt || row.bucket === "failed" || row.bucket === "cancelled") {
    const cancelled = row.bucket === "cancelled";
    steps.push({
      key: cancelled ? "cancelled" : "failed",
      label: cancelled ? t("timeline_step_cancelled") : t("timeline_step_failed"),
      caption: row.failureReason,
      time: row.failedAt,
      tone: cancelled ? "cancelled" : "failed",
    });
  } else if (row.settledAt) {
    steps.push(
      { key: "processing", label: t("timeline_step_processing"), caption: null, time: null, tone: "done" },
      {
        key: "settled",
        label: row.direction === "payins" ? t("timeline_step_credited") : t("timeline_step_paid"),
        caption: null,
        time: row.settledAt,
        tone: "done",
      },
    );
  } else {
    steps.push({
      key: "processing",
      label: t("timeline_step_processing"),
      caption: null,
      time: null,
      tone: "active",
    });
  }
  return <Steps steps={steps} />;
}
